import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Check, X, CreditCard, BadgeCheck, Lock } from 'lucide-react';
import { products } from '../../stripe-config';
import { hikerApi } from '../../services/hikerApi';
import { getPlanBadgeStyles } from '../../utils/planStyles';

interface PaymentProps {}

interface ProfileData {
  username: string;
  name: string;
  avatar: string;
  followers: string;
  following: string;
  posts: number;
  biography: string;
  isVerified: boolean;
  isPrivate: boolean;
}

export function Payment({}: PaymentProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [planName, setPlanName] = useState<string | null>(null);
  const [period, setPeriod] = useState<'monthly' | 'annual'>('annual');
  const [closingId, setClosingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const username = params.get('username');
    const plan = params.get('plan');
    const periodParam = params.get('period');
    const id = params.get('id');

    if (!username || !plan || !periodParam || !id) {
      setError('Invalid payment link. Missing required parameters.');
      setIsLoading(false);
      return;
    }

    setPlanName(plan);
    setPeriod(periodParam === 'annual' ? 'annual' : 'monthly');
    setClosingId(id);

    const loadProfile = async () => {
      try {
        const cleanUsername = username.replace('@', '').trim();
        const result = await hikerApi.getAccountInfo(cleanUsername);
        
        if (result.error || !result.data) {
          setError(result.message || 'Failed to fetch account information');
          return;
        }

        setProfile({
          username: '@' + result.data.username,
          name: result.data.fullName || result.data.username,
          avatar: result.data.profilePicUrl || 'https://via.placeholder.com/150',
          followers: result.data.followers || '0',
          following: result.data.following || '0',
          posts: result.data.posts || 0,
          biography: result.data.biography || '',
          isVerified: result.data.isVerified || false,
          isPrivate: result.data.isPrivate || false
        });
      } catch (err) {
        console.error('Error loading profile:', err);
        setError('Failed to load Instagram profile');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [location]);

  const handleCheckout = async () => {
    try {
      setIsCheckingOut(true);
      
      if (!planName || !profile || !closingId) {
        throw new Error('Missing required information for checkout');
      }
      
      // Get the product configuration
      const product = products[planName as keyof typeof products];
      if (!product) {
        throw new Error(`Plan "${planName}" not found`);
      }
      
      // Determine currency based on browser language
      const userLanguage = navigator.language || 'en-US';
      const currency = userLanguage.startsWith('fr') ? 'EUR' : 'USD';
      
      // Get the price ID
      const priceId = product[currency][period].priceId;
      if (!priceId) {
        throw new Error(`Price ID not found for ${planName} (${currency}, ${period})`);
      }

      // Create a checkout session
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout-public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache'
        },
        body: JSON.stringify({
          price_id: priceId,
          mode: product.mode,
          success_url: `https://app.amplr.ai/payment-success?id=${closingId}`,
          cancel_url: `https://app.amplr.ai/payment-cancel?id=${closingId}`,
          allow_promotion_codes: true,
          automatic_tax: true,
          tax_id_collection: true,
          account_id: null,
          closing_id: closingId,
          include_trial: true,
          profile_data: {
            username: profile.username,
            name: profile.name,
            avatar: profile.avatar || 'https://via.placeholder.com/150'
          }
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create checkout session';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.details || errorMessage;
          console.error('Checkout error response:', JSON.stringify(errorData, null, 2));
        } catch (e) {
          console.error('Error parsing error response:', e);
        }
        console.error('Checkout error:', errorMessage, 'Status:', response.status, response.statusText, 'URL:', `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout-public`);
        throw new Error(errorMessage);
      }

      const { url } = await response.json();
      if (!url) {
        throw new Error('No checkout URL returned');
      }

      // Redirect to Stripe checkout
      window.location.href = url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setError(error instanceof Error ? error.message : 'Failed to create checkout session');
      setIsCheckingOut(false);
    }
  };

  // Get price display based on plan and period
  const getPriceDisplay = () => {
    if (!planName) return '';
    
    const product = products[planName as keyof typeof products];
    if (!product) return '';
    
    // Determine currency based on browser language
    const userLanguage = navigator.language || 'en-US';
    const currency = userLanguage.startsWith('fr') ? 'EUR' : 'USD';
    
    return period === 'annual' 
      ? product[currency].annual.price
      : product[currency].monthly.price;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#121212]">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-200">Loading...</h2>
          <p className="text-gray-600 dark:text-gray-400">Preparing your payment page</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#121212] p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">Error</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <a 
            href="/"
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  if (!profile || !planName || !closingId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-[#121212] p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <X size={32} className="text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-2">Invalid Link</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">This payment link is invalid or has expired.</p>
          <a 
            href="/"
            className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#121212]">
      <div className="flex flex-col lg:flex-row">
        {/* Profile Information */}
        <div className="w-full lg:w-1/2 p-6 lg:p-12 flex items-center justify-center">
          <div className="max-w-md w-full">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Amplr AI for {profile.name}
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                You're about to supercharge your Instagram growth
              </p>
            </div>

            <div className="bg-white dark:bg-[#1c1c1c] rounded-lg border border-gray-200 dark:border-gray-800 p-6 mb-6 shadow-sm">
              <div className="flex items-start gap-4 mb-4">
                <img
                  src={profile.avatar}
                  alt={profile.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 dark:text-gray-200">
                      {profile.name}
                    </h4>
                    {profile.isVerified && (
                      <span className="text-blue-500">
                        <BadgeCheck size={16} className="text-blue-500" />
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {profile.username}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 py-3 border-y border-gray-200 dark:border-gray-800">
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-200">
                    {profile.posts}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    posts
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-200">
                    {profile.followers}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    followers
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-gray-900 dark:text-gray-200">
                    {profile.following}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    following
                  </div>
                </div>
              </div>

              {profile.biography && (
                <div className="mt-4 text-sm text-gray-700 dark:text-gray-300">
                  {profile.biography.split('\n').map((line, index) => (
                    <span key={index}>
                      {line}
                      {index < profile.biography.split('\n').length - 1 && <br />}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className="w-full lg:w-1/2 bg-white dark:bg-[#1c1c1c] p-6 lg:p-12 flex items-center justify-center lg:min-h-screen border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-800">
          <div className="max-w-md w-full">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-200 mb-6">
              Plan Summary
            </h2>
            
            <div className="mb-6 p-5 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-[#252525]">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadgeStyles(planName)}`}>
                    {planName}
                  </div>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {period === 'annual' ? 'Annual billing' : 'Monthly billing'}
                  </span>
                </div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {getPriceDisplay()}
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                    /mo
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                {products[planName as keyof typeof products]?.features.slice(0, 5).map((feature, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <BadgeCheck size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
              
              {period === 'annual' && (
                <div className="text-sm text-emerald-500 font-medium mt-2">
                  Save up to 40% with annual billing
                </div>
              )}
            </div>
            
            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-50 border border-red-100 text-red-600 text-sm">
                {error}
              </div>
            )}
            
            <button
              onClick={handleCheckout}
              disabled={isCheckingOut}
              className="w-full bg-orange-500 text-white rounded-lg py-3 font-medium hover:bg-orange-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
            >
              {isCheckingOut ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <CreditCard size={18} />
                  <span>Proceed to Checkout</span>
                </>
              )}
            </button>
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-4">
              <Lock size={14} />
              <span>Secure payment processed by Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}